const vendorLink = (vendorCategory) => ({
    kind: 'VENDOR_LINK',
    vendorCategory,
});
/**
 * Default wedding questionnaire template, seeded once. Producers can edit it
 * (or add new templates) from the Templates page afterwards.
 */
export const WEDDING_QUESTIONNAIRE_TEMPLATE = {
    templateName: 'Wedding Customs & Planning Questionnaire',
    title: 'Wedding Customs & Planning Questionnaire',
    description: 'To personalize your wedding and ensure every detail is planned according to ' +
        "your family's traditions and preferences, please complete the questionnaire " +
        'below. Please answer only the questions that apply to your wedding.',
    sections: [
        {
            name: 'Wedding Details',
            sortOrder: 0,
            questions: [
                {
                    prompt: "Bride's full name",
                    questionType: 'TEXT',
                    dataLink: { kind: 'EVENT_TAG', tagCode: 'bride_name' },
                },
                {
                    prompt: "Groom's full name",
                    questionType: 'TEXT',
                    dataLink: { kind: 'EVENT_TAG', tagCode: 'groom_name' },
                },
                {
                    prompt: 'Wedding Date',
                    questionType: 'DATE',
                    dataLink: { kind: 'EVENT_FIELD', field: 'startAt' },
                },
                {
                    prompt: 'Wedding Venue and address',
                    questionType: 'TEXT',
                    dataLink: { kind: 'EVENT_FIELD', field: 'venue' },
                },
                {
                    prompt: 'What time does the Kabbalat Panim / Cocktail Hour begin?',
                    questionType: 'TIME',
                    dataLink: { kind: 'TIMELINE_TIME', entryTitleMatch: 'Kabbalat Panim' },
                },
                {
                    prompt: 'What time is the Chuppah?',
                    questionType: 'TIME',
                    dataLink: { kind: 'TIMELINE_TIME', entryTitleMatch: 'Chuppah' },
                },
                {
                    prompt: 'What time is "Simchat Chatan Kalah"?',
                    questionType: 'TIME',
                    dataLink: { kind: 'TIMELINE_TIME', entryTitleMatch: 'Simchat Chatan' },
                },
                {
                    prompt: 'Please provide the address where the bride will be getting ready.',
                    questionType: 'ADDRESS',
                    dataLink: { kind: 'EVENT_TAG', tagCode: 'bride_prep_address' },
                },
                {
                    prompt: 'Please provide the address where the groom will be getting ready.',
                    questionType: 'ADDRESS',
                    dataLink: { kind: 'EVENT_TAG', tagCode: 'groom_prep_address' },
                },
            ],
        },
        {
            name: 'Ceremony & Customs',
            sortOrder: 1,
            questions: [
                {
                    prompt: 'Who will escort the bride and groom to the chuppah?',
                    questionType: 'SINGLE_CHOICE',
                    config: {
                        choices: [
                            'Both parents escort both the bride and groom',
                            'Mothers escort the bride, fathers escort the groom',
                        ],
                        allowOther: true,
                    },
                },
                { prompt: 'How many grandmothers will walk down the aisle?', questionType: 'NUMBER' },
                { prompt: 'How many grandfathers will walk down the aisle?', questionType: 'NUMBER' },
                { prompt: 'Would you like to have a Badeken ceremony?', questionType: 'YES_NO' },
                {
                    prompt: 'Would you like the groom to place the veil over the bride during the Badeken or while walking down the aisle?',
                    questionType: 'SINGLE_CHOICE',
                    config: { choices: ['During the Badeken', 'While walking down the aisle'] },
                },
                {
                    prompt: 'Would you like the groom to recite his personal prayers before or after the Kabbalat Panim?',
                    questionType: 'SINGLE_CHOICE',
                    config: { choices: ['Before the Kabbalat Panim', 'After the Kabbalat Panim'] },
                },
                {
                    prompt: 'Will the groom put on his kittel before the Badeken or after the Badeken (before the chuppah)?',
                    questionType: 'SINGLE_CHOICE',
                    config: { choices: ['Before the Badeken', 'After the Badeken (before the chuppah)'] },
                },
                {
                    prompt: 'Do you have any special family, religious, or community customs that we should be aware of?',
                    questionType: 'LONG_TEXT',
                },
                { prompt: 'Do you have the custom of using a silver spoon?', questionType: 'YES_NO' },
            ],
        },
        {
            name: 'Wedding Day Logistics',
            sortOrder: 2,
            questions: [
                { prompt: 'Will vendor meals be provided?', questionType: 'YES_NO' },
                {
                    prompt: 'Would you like me to manage vendor payments on the wedding day?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'Are there any surprises planned for the bride or groom?',
                    questionType: 'LONG_TEXT',
                },
                {
                    prompt: 'Will the bride need to change her veil during the wedding?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'Will the bride need to change her shoes during the wedding?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'Will the bride have a dresser or bridal attendant?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'Will the hair and makeup artist remain through the Yichud and be available afterward for touch-ups?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'Would you like to distribute guest favors or swag bags at the end of the wedding?',
                    questionType: 'YES_NO',
                },
            ],
        },
        {
            name: 'Photography Timeline',
            sortOrder: 3,
            questions: [
                {
                    prompt: 'Would you like the bride and groom to meet and take photos together before the chuppah?',
                    questionType: 'YES_NO',
                },
                { prompt: 'Would you like to have a First Look?', questionType: 'YES_NO' },
                {
                    prompt: "How many immediate family members are in the bride's family?",
                    questionType: 'NUMBER',
                },
                {
                    prompt: "How many immediate family members are in the groom's family?",
                    questionType: 'NUMBER',
                },
                { prompt: 'Would you like to take extended family photos?', questionType: 'YES_NO' },
                {
                    prompt: 'Are there any specific family photo requests we should know about?',
                    questionType: 'LONG_TEXT',
                },
            ],
        },
        {
            name: 'Reception Timeline',
            sortOrder: 4,
            questions: [
                { prompt: 'Will you have a First Dance?', questionType: 'YES_NO' },
                { prompt: 'Will there be a Mother & Groom dance?', questionType: 'YES_NO' },
                { prompt: 'Will there be a Father & Bride dance?', questionType: 'YES_NO' },
                {
                    prompt: 'Will there be speeches? If yes, who will be speaking?',
                    questionType: 'LONG_TEXT',
                },
                { prompt: 'Will you be showing a video or slideshow?', questionType: 'YES_NO' },
                { prompt: 'Will you have a cake-cutting ceremony?', questionType: 'YES_NO' },
                { prompt: 'Will you have Sheva Brachot?', questionType: 'YES_NO' },
            ],
        },
        {
            name: 'Music & Entertainment',
            sortOrder: 5,
            questions: [
                {
                    prompt: 'Will the same band, DJ, or musicians perform throughout the entire wedding?',
                    questionType: 'YES_NO',
                },
                {
                    prompt: 'If there will be a change in entertainment during the event, please explain:',
                    questionType: 'LONG_TEXT',
                },
            ],
        },
        {
            name: 'Chuppah Setup',
            sortOrder: 6,
            questions: [
                {
                    prompt: 'Will chairs be needed under the chuppah for grandparents or other elderly family members?',
                    questionType: 'YES_NO',
                },
                { prompt: 'Will candles be used?', questionType: 'YES_NO' },
                { prompt: "Number of candles on the groom's side:", questionType: 'NUMBER' },
                { prompt: "Number of candles on the bride's side:", questionType: 'NUMBER' },
                {
                    prompt: 'Will you need a plate for the glass-breaking ceremony?',
                    questionType: 'YES_NO',
                },
                { prompt: 'Will you need a glass for the groom to break?', questionType: 'YES_NO' },
                { prompt: 'Will you need candles?', questionType: 'YES_NO' },
            ],
        },
        {
            name: 'Items & Responsibilities',
            description: 'Please provide the name and phone number of the person responsible for bringing each of the following:',
            sortOrder: 7,
            questions: [
                { prompt: 'Kittel', questionType: 'CONTACT' },
                { prompt: "Bride's face covering (veil)", questionType: 'CONTACT' },
                { prompt: 'Ketubah', questionType: 'CONTACT' },
                { prompt: 'Marriage License', questionType: 'CONTACT' },
                { prompt: 'Rings', questionType: 'CONTACT' },
                { prompt: 'Glass for breaking (if provided by the family)', questionType: 'CONTACT' },
            ],
        },
        {
            name: 'Vendors',
            description: 'Please provide the names and contact information for all of your vendors:',
            sortOrder: 8,
            questions: [
                { prompt: 'Caterer', questionType: 'VENDOR', dataLink: vendorLink('Catering') },
                { prompt: 'Rabbi / Officiant', questionType: 'VENDOR', dataLink: vendorLink('Officiant') },
                { prompt: 'Photographer', questionType: 'VENDOR', dataLink: vendorLink('Photography') },
                { prompt: 'Videographer', questionType: 'VENDOR', dataLink: vendorLink('Videography') },
                { prompt: 'Band / DJ', questionType: 'VENDOR', dataLink: vendorLink('Music') },
                { prompt: 'Florist', questionType: 'VENDOR', dataLink: vendorLink('Flowers') },
                { prompt: 'Hair Stylist', questionType: 'VENDOR', dataLink: vendorLink('Hair & Makeup') },
                { prompt: 'Makeup Artist', questionType: 'VENDOR', dataLink: vendorLink('Hair & Makeup') },
                { prompt: 'Rental Company', questionType: 'VENDOR', dataLink: vendorLink('Rental') },
                {
                    prompt: 'Transportation',
                    questionType: 'VENDOR',
                    dataLink: vendorLink('Transportation'),
                },
                {
                    prompt: 'Cake / Dessert Vendor',
                    questionType: 'VENDOR',
                    dataLink: vendorLink('Catering'),
                },
                { prompt: 'AV Company', questionType: 'VENDOR', dataLink: vendorLink('Sound') },
                { prompt: 'Other', questionType: 'VENDOR', dataLink: vendorLink('Other') },
            ],
        },
        {
            name: 'Additional Notes',
            sortOrder: 9,
            questions: [
                {
                    prompt: 'Please use the space below to share any additional information, special requests, ' +
                        'traditions, or important details that will help us make your wedding day seamless ' +
                        'and memorable.',
                    questionType: 'LONG_TEXT',
                },
            ],
        },
    ].map((section, index) => ({
        ...section,
        sortOrder: index,
        questions: (section.questions ?? []).map((question, qIndex) => ({
            ...question,
            sortOrder: qIndex,
        })),
    })),
};
//# sourceMappingURL=questionnaire-templates.js.map